import React, {useEffect, useState, useCallback} from "react";
import {Table, Typography, Spin, message, Select, Row, Col, Modal, Button} from "antd";
import {FilePdfOutlined} from "@ant-design/icons";
import {useMediaQuery} from "react-responsive";
import {motion} from "framer-motion";
import {useSelector} from "react-redux";
import {jsPDF} from "jspdf";
import autoTable from "jspdf-autotable";
import statisticsService from "../services/StatisticsService";
import peopleService from "../services/PeopleService";
import theme from "../theme";

const formatAddress = (record) => {
    const street = record.street || '';
    const exteriorNumber = record.exteriorNumber || '';
    const interiorNumber = record.interiorNumber || '';
    const neighborhood = record.neighborhood || '';
    const zipCode = record.zipCode || '';
    const number = exteriorNumber && interiorNumber
        ? `#${exteriorNumber}-${interiorNumber}`
        : exteriorNumber ? `#${exteriorNumber}` : interiorNumber ? `#${interiorNumber}` : '';
    return `${street} ${number}, ${neighborhood}, CP ${zipCode}`.trim();
};

const {Title} = Typography;

const PromocionPage = () => {
    const [stats, setStats] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);

    const [selectedSections, setSelectedSections] = useState([]);

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedSection, setSelectedSection] = useState(null);
    const [modalPeople, setModalPeople] = useState([]);
    const [modalLoading, setModalLoading] = useState(false);
    const [modalPage, setModalPage] = useState(1);
    const [modalPageSize, setModalPageSize] = useState(10);
    const [modalTotal, setModalTotal] = useState(0);

    const isMobile = useMediaQuery({maxWidth: 800});
    const auth = useSelector((state) => state.auth || {});
    const user = auth.user || {};
    const {entityId, route = []} = user;

    const sectionOptions = [...new Set(route)].sort((a, b) => a - b);

    const fetchData = useCallback(
        async ({sectionsFilter = null, page = 1, size = 10}) => {
            const finalSectionsFilter = sectionsFilter?.length ? sectionsFilter : (route.length ? route : null);

            if (!entityId) return;
            setLoading(true);

            try {
                const response = await statisticsService.getStatistics(
                    entityId,
                    null,
                    finalSectionsFilter ? finalSectionsFilter.join(",") : null,
                    page,
                    size
                );
                const {data, meta} = response.data;

                setStats(data);
                setCurrentPage(page);
                setPageSize(size);
                setTotalItems(meta.totalItems);
            } catch (error) {
                console.error("Error fetching statistics:", error);
                message.error("Error al obtener las estadísticas.");
            } finally {
                setLoading(false);
            }
        },
        [entityId, route]
    );

    const fetchModalPeople = useCallback(async (sectionId, page, size) => {
        setModalLoading(true);
        try {
            const response = await peopleService.getAll(page, size, {
                sectionId: {value: [sectionId], operator: "in"},
            });
            setModalPeople(response.data);
            setModalTotal(response.meta.totalItems);
            setModalPage(page);
            setModalPageSize(size);
        } catch (error) {
            console.error("Error fetching people:", error);
            message.error("Error al obtener los convencidos.");
        } finally {
            setModalLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData({
            sectionsFilter: selectedSections,
            page: currentPage,
            size: pageSize,
        }).catch(console.error);
    }, [fetchData, selectedSections, currentPage, pageSize]);

    const handleSectionChange = (values) => {
        setSelectedSections(values);
        setCurrentPage(1);
    };

    const handleTableChange = (newPagination) => {
        fetchData({
            page: newPagination.current,
            size: newPagination.pageSize,
            sectionsFilter: selectedSections,
        }).catch(console.error);
    };

    const handleRowClick = (record) => {
        setSelectedSection(record.sectionId);
        setIsModalVisible(true);
        fetchModalPeople(record.sectionId, 1, 10);
    };

    const handleModalClose = () => {
        setIsModalVisible(false);
        setSelectedSection(null);
        setModalPeople([]);
        setModalPage(1);
        setModalTotal(0);
    };

    const handleModalTableChange = (pagination) => {
        fetchModalPeople(selectedSection, pagination.current, pagination.pageSize);
    };

    const exportModalPDF = async () => {
        const hide = message.loading({content: "Generando PDF...", key: "pdf"});
        try {
            const response = await peopleService.getAll(1, 10000, {
                sectionId: {value: [selectedSection], operator: "in"},
            });
            const allPeople = response.data || [];

            if (!allPeople.length) {
                message.warning({content: "No hay datos para exportar", key: "pdf"});
                return;
            }

            const doc = new jsPDF();
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const title = `C-${selectedSection}`;
            const dateStr = new Date().toLocaleString("es-MX", {dateStyle: "long", timeStyle: "short"});

            const drawHeader = () => {
                doc.setFontSize(14);
                doc.setTextColor(80, 80, 80);
                doc.text(title, pageWidth / 2, 14, {align: "center"});
                doc.setFontSize(9);
                doc.setTextColor(120, 120, 120);
                doc.text(`Generado: ${dateStr}`, pageWidth / 2, 21, {align: "center"});
            };

            drawHeader();

            const rows = allPeople.map((p) => [
                p.sectionId,
                `${p.name || ""} ${p.firstLastName || ""} ${p.secondLastName || ""}`.trim(),
                formatAddress(p),
                p.phone || "",
            ]);

            autoTable(doc, {
                head: [["Sección", "Nombre completo", "Domicilio", "Teléfono"]],
                body: rows,
                startY: 26,
                margin: {top: 30},
                headStyles: {fillColor: [100, 100, 100], textColor: 255, fontStyle: "bold"},
                alternateRowStyles: {fillColor: [240, 240, 240]},
                styles: {fontSize: 8, cellPadding: 3, textColor: [60, 60, 60]},
                didDrawPage: (data) => {
                    if (data.pageNumber > 1) drawHeader();
                    doc.setFontSize(8);
                    doc.setTextColor(150, 150, 150);
                    doc.text(`Página ${data.pageNumber}`, pageWidth / 2, pageHeight - 8, {align: "center"});
                },
            });

            const fileName = `convencidos_seccion_${selectedSection}_${new Date().toISOString().slice(0, 10)}.pdf`;
            doc.save(fileName);
            message.success({content: "PDF generado correctamente", key: "pdf"});
        } catch (error) {
            console.error("exportModalPDF error:", error);
            message.error({content: "Error al generar PDF", key: "pdf"});
        }
    };

    const columns = [
        {title: "Entidad", dataIndex: "entityId", key: "entityId"},
        {title: "Sección", dataIndex: "sectionId", key: "sectionId"},
        {title: "Meta mínima", dataIndex: "goal", key: "goal"},
        {title: "Asistencia", dataIndex: "rollCallCount", key: "rollCallCount"},
        {title: "Avance", dataIndex: "totalCount", key: "totalCount"},
        {
            title: "Avance (%)",
            dataIndex: "progressPercentage",
            key: "progressPercentage",
            render: (value) => `${value.toFixed(2)}%`,
        },
    ];

    const modalColumns = [
        {title: "Sección", dataIndex: "sectionId", key: "sectionId", width: 90},
        {
            title: "Nombre completo",
            key: "fullName",
            render: (_, record) =>
                `${record.name || ''} ${record.firstLastName || ''} ${record.secondLastName || ''}`.trim(),
        },
        {
            title: "Domicilio",
            key: "address",
            render: (_, record) => formatAddress(record),
        },
        {title: "Teléfono", dataIndex: "phone", key: "phone", width: 130},
    ];

    return (
        <>
            <motion.div
                initial={{opacity: 0, y: -10}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.5}}
                style={{
                    maxWidth: isMobile ? "100%" : "90%",
                    width: "90vw",
                    margin: "20px auto",
                    background: theme.colors.card,
                    padding: "20px",
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                    overflowX: "auto",
                }}
            >
                <div style={{textAlign: "center", marginBottom: "20px"}}>
                    <Title level={2}>📊 Avance de Promoción</Title>
                </div>

                <Row gutter={[16, 16]} style={{marginBottom: "20px"}}>
                    <Col xs={24}>
                        <label style={{display: "block", marginBottom: "8px", fontWeight: "bold"}}>
                            Secciones:
                        </label>
                        <Select
                            mode="multiple"
                            allowClear
                            placeholder="Todas las secciones"
                            value={selectedSections}
                            onChange={handleSectionChange}
                            style={{width: "100%"}}
                            disabled={loading}
                            options={sectionOptions.map(id => ({
                                label: `Sección ${id}`,
                                value: id,
                            }))}
                        />
                    </Col>
                </Row>

                <Spin spinning={loading}>
                    <Table
                        columns={columns}
                        dataSource={stats.map((stat) => ({
                            key: `${stat.id}-${stat.sectionId}`,
                            ...stat,
                            progressPercentage: ((stat.totalCount || 0) / (stat.goal || 1)) * 100,
                        }))}
                        rowKey="key"
                        pagination={{
                            current: currentPage,
                            pageSize: pageSize,
                            total: totalItems,
                            showSizeChanger: true,
                            pageSizeOptions: ["5", "10", "20"],
                        }}
                        onChange={handleTableChange}
                    />
                </Spin>
            </motion.div>

            <Modal
                title={`Convencidos — Sección ${selectedSection}`}
                open={isModalVisible}
                onCancel={handleModalClose}
                footer={
                    <Button
                        type="primary"
                        danger
                        icon={<FilePdfOutlined />}
                        onClick={exportModalPDF}
                    >
                        Descargar PDF
                    </Button>
                }
                width={isMobile ? "100%" : 800}
                destroyOnClose
            >
                <Spin spinning={modalLoading}>
                    <Table
                        columns={modalColumns}
                        dataSource={modalPeople.map((p) => ({key: p.id, ...p}))}
                        rowKey="key"
                        pagination={{
                            current: modalPage,
                            pageSize: modalPageSize,
                            total: modalTotal,
                            showSizeChanger: true,
                            pageSizeOptions: ["5", "10", "20"],
                        }}
                        onChange={handleModalTableChange}
                        scroll={{x: true}}
                        size="small"
                    />
                </Spin>
            </Modal>
        </>
    );
};

export default PromocionPage;
